// UI values only, scoped to one authenticated menu visit in this browser tab.
export const SCREEN_STATE_KEY = "academyScreenState.v1";
const resets = new WeakMap();
const emptySnapshot = {};
const fields = new Set([
  "query", "schoolId", "schoolStage", "grade", "status", "page", "pageSize",
  "year", "academicYear", "termId", "termType", "subjectId", "teacherId",
  "courseId", "folderId", "categoryId", "type", "month", "activeTab", "date",
]);
const validScope = (key) => typeof key === "string" && /^[a-zA-Z0-9:_-]{1,180}$/.test(key)
  && !["__proto__", "constructor", "prototype"].includes(key);
const target = (route) => route.kind && route.id
  ? `${route.kind}:${route.id}${route.month ? `:${route.month}` : ""}` : "";
export const screenDetailScope = (route) => target(route) ? `detail:${target(route)}` : "";
export function screenOwner(identity) {
  const values = [identity?.userId, identity?.academyId, identity?.role];
  return values.every((v) => typeof v === "string" && /^[a-zA-Z0-9_-]{1,128}$/.test(v))
    ? values.join(":") : "";
}
function cleanValue(value) {
  if (!value || Object.getPrototypeOf(value) !== Object.prototype) return null;
  const entries = Object.entries(value);
  if (entries.length > fields.size) return null;
  const clean = {};
  for (const [key, v] of entries) {
    if (!fields.has(key)) continue;
    if (v === null || typeof v === "boolean" ||
      (typeof v === "string" && v.length <= 200) ||
      (typeof v === "number" && Number.isFinite(v))) clean[key] = v;
  }
  return clean;
}
function cleanData(raw) {
  if (!raw || Object.getPrototypeOf(raw) !== Object.prototype) return {};
  const clean = {};
  for (const [key, value] of Object.entries(raw).slice(-64)) {
    if (!validScope(key)) continue;
    const v = cleanValue(value);
    if (v) clean[key] = v;
  }
  return clean;
}
function withoutDetails(data) {
  return Object.fromEntries(Object.entries(data).filter(([key]) => !key.startsWith("detail:")));
}
export function clearScreenState(browser = globalThis.window) {
  if (!browser) return;
  try { browser.sessionStorage.removeItem(SCREEN_STATE_KEY); } catch { /* storage may be blocked */ }
  for (const clear of resets.get(browser) || []) clear();
}
export function createScreenState(browser, identity, initialRoute) {
  const owner = screenOwner(identity);
  let view = initialRoute.view, detail = target(initialRoute), data = {}, active = !!owner;
  let visit = {};
  const listeners = new Set();
  try {
    const raw = browser.sessionStorage.getItem(SCREEN_STATE_KEY);
    const saved = raw && raw.length <= 50000 ? JSON.parse(raw) : null;
    if (owner && saved?.version === 1 && saved.owner === owner && saved.view === view) {
      data = cleanData(saved.data);
      if (saved.target !== detail) data = withoutDetails(data);
    }
  } catch { /* Corrupt/blocked storage starts from safe defaults. */ }
  const emit = () => { for (const fn of listeners) fn(); };
  function save() {
    if (!active) return;
    try {
      const raw = JSON.stringify({ version: 1, owner, view, target: detail, data });
      if (raw.length <= 50000) browser.sessionStorage.setItem(SCREEN_STATE_KEY, raw);
      else browser.sessionStorage.removeItem(SCREEN_STATE_KEY);
    } catch {
      // Do not restore an older saved value after a failed write.
      try { browser.sessionStorage.removeItem(SCREEN_STATE_KEY); } catch { /* blocked */ }
    }
  }
  const reset = () => { active = false; visit = {}; data = {}; emit(); };
  const store = {
    connect() {
      const callbacks = resets.get(browser) || new Set();
      resets.set(browser, callbacks);
      callbacks.add(reset);
      save();
      return () => callbacks.delete(reset);
    },
    transition(next) {
      const nextDetail = target(next);
      if (next.view !== view) { visit = {}; data = {}; }
      else if (nextDetail !== detail) data = withoutDetails(data);
      view = next.view;
      detail = nextDetail;
      save();
      emit();
    },
    getSnapshot: () => data,
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    read(scope) { return validScope(scope) ? data[scope] : undefined; },
    write(scope, value) {
      if (!active || !validScope(scope)) return;
      const clean = cleanValue(value);
      if (!clean) return;
      data = { ...data, [scope]: clean };
      if (Object.keys(data).length > 64) {
        const [oldest] = Object.keys(data);
        delete data[oldest];
      }
      save();
      emit();
    },
    bind(route) {
      const boundVisit = visit, boundDetail = target(route);
      const current = () => active && boundVisit === visit && route.view === view;
      const readable = (scope) => validScope(scope) && current() && (!scope.startsWith("detail:") ||
        (boundDetail === detail && scope === `detail:${boundDetail}`));
      return {
        subscribe: store.subscribe,
        getSnapshot: () => current() ? data : emptySnapshot,
        read: (scope) => readable(scope) ? store.read(scope) : undefined,
        write: (scope, value) => { if (readable(scope)) store.write(scope, value); },
      };
    },
  };
  return store;
}
